import { z } from "zod";
/**
 * Notes — markdown workspace SSOT.
 *
 * Canon: docs/design-canon/04-shared/ssot.md (Zod first; routes
 * import these and use zValidator), docs/enhancements/feature-
 * sketches-2026-05.md §1.
 */
// ─── Enums ─────────────────────────────────────────────────
export const NoteKindSchema = z.enum(["note", "daily", "meeting", "research", "idea"]);
export const NoteLinkTargetEntitySchema = z.enum(["contact", "company", "project"]);
// ─── Note links (resolved [[wikilinks]]) ───────────────────
export const NoteLinkSchema = z.object({
    id: z.string().uuid(),
    sourceNoteId: z.string().uuid(),
    targetNoteId: z.string().uuid().nullable(),
    targetEntityType: NoteLinkTargetEntitySchema.nullable(),
    targetEntityId: z.string().uuid().nullable(),
    rawText: z.string().min(1).max(300),
    createdAt: z.string().datetime(),
});
/**
 * Resolved-link summary for the detail page. Includes either the
 * target note's title OR the entity's name, so the renderer doesn't
 * have to make follow-up queries.
 */
export const NoteLinkResolvedSchema = NoteLinkSchema.extend({
    resolvedLabel: z.string().nullable(),
});
/**
 * Backlink summary — a note linking TO this one. Carries the source
 * note's id + title so the "linked from" panel can render without a
 * follow-up.
 */
export const NoteBacklinkSchema = z.object({
    sourceNoteId: z.string().uuid(),
    sourceNoteTitle: z.string(),
    rawText: z.string(),
});
// ─── Note core ─────────────────────────────────────────────
export const NoteSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(300),
    body: z.string(),
    kind: NoteKindSchema,
    noteDate: z.string().nullable(),
    tags: z.array(z.string().min(1).max(60)),
    pinned: z.boolean(),
    createdBy: z.string().email(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const NoteCreateSchema = z.object({
    title: z.string().min(1).max(300),
    body: z.string().max(50_000).default(""),
    kind: NoteKindSchema.default("note"),
    noteDate: z.string().date().optional(),
    tags: z.array(z.string().min(1).max(60)).max(20).default([]),
    pinned: z.boolean().default(false),
});
export const NoteUpdateSchema = z
    .object({
    title: z.string().min(1).max(300),
    body: z.string().max(50_000),
    kind: NoteKindSchema,
    noteDate: z.string().date().nullable(),
    tags: z.array(z.string().min(1).max(60)).max(20),
    pinned: z.boolean(),
})
    .partial();
// ─── Listing / filtering ───────────────────────────────────
export const NoteListQuerySchema = z.object({
    /** Free-text search over title + body via Postgres FTS (websearch). */
    search: z.string().min(1).max(200).optional(),
    /** Filter by tag (single tag v1; multi-tag AND/OR comes later). */
    tag: z.string().min(1).max(60).optional(),
    kind: NoteKindSchema.optional(),
    pinned: z.coerce.boolean().optional(),
    includeArchived: z.coerce.boolean().default(false),
    archivedOnly: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// List items carry the full Note shape — body is used for preview
// snippets in the list view. If list-perf becomes a problem we'll
// project to a smaller shape later.
export const NoteListResponseSchema = z.object({
    notes: z.array(NoteSchema),
    total: z.number().int(),
});
// ─── Detail (note + resolved links + backlinks) ────────────
export const NoteDetailResponseSchema = z.object({
    note: NoteSchema,
    outgoingLinks: z.array(NoteLinkResolvedSchema),
    backlinks: z.array(NoteBacklinkSchema),
});
//# sourceMappingURL=notes.js.map